import { Box, Grid, IconButton, Typography } from "@mui/material";
import ControlledDateTimePicker from "components/Controller/ControlledDateTimePicker";
import ControlledNumberTextField from "components/Controller/ControlledNumberTextField";
import ControlledTextField from "components/Controller/ControlledTextField";
import { CustomizedBox } from "components/Custom/CustomizedBox";
import { CustomizedTooltip } from "components/Custom/CustomizedTooltip";
import { useDisable } from "hooks/use-disable";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { IDeliveryTrip } from "types/Logistic/deliveryTrip";
import RestartAltOutlinedIcon from "@mui/icons-material/RestartAltOutlined";
import { createGraphQLClientWithMiddleware } from "services/graphqlClient";
import {
  useVehicleCurrentKiloQuery,
  VehicleCurrentKiloQuery,
} from "generated/vehicle";
import { useCallback } from "react";

// 86d37db3t — every section is visible in every status. The old
// `["delivering","finished","cancelled"].includes(status)` gate is gone.
// The Fetch button (start_kilometer auto-fetch) still exists but is no
// longer auto-triggered on mount; the field defaults blank.
const DateDistanceInfo = () => {
  const {
    control,
    formState: { errors },
    setValue,
    getValues,
  } = useFormContext<IDeliveryTrip>();
  const { t } = useTranslation();
  const [disabled] = useDisable();

  const vehicleId = useWatch({
    control,
    name: "vehicle_id",
  });

  const graphQLClient = createGraphQLClientWithMiddleware("sales");

  const { refetch: refetchCurrentKilo } =
    useVehicleCurrentKiloQuery<VehicleCurrentKiloQuery>(
      graphQLClient,
      {
        vehicleFindUniqueId: vehicleId ?? 0,
      },
      {
        enabled: false,
      }
    );

  // Manual Fetch — click the icon to pull the vehicle's current kilometer.
  // (Auto-fetch on Copy/state removed per sheet — the user chooses when.)
  const fetchCurrentKilo = useCallback(async () => {
    const { data } = await refetchCurrentKilo();
    const start_kilometer = data?.VehicleFindUnique?.current_kilometer ?? 0;
    setValue("start_kilometer", start_kilometer);

    const end_kilometer = getValues("end_kilometer") || 0;
    if (end_kilometer) {
      setValue("total_kilometer_distance", end_kilometer - start_kilometer);
    }
  }, [getValues, refetchCurrentKilo, setValue]);

  return (
    <CustomizedBox>
      <Typography fontWeight={600} mb={1}>
        {t("logistic.delivery_trip.datetime")}
      </Typography>
      <Grid container spacing={1.5}>
        <Grid item xs={12} sm={12} md={4}>
          <ControlledDateTimePicker
            control={control}
            name="plan_start_date"
            label={t("logistic.delivery_trip.plan_start_date")}
            viewMode={disabled}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={4}>
          <ControlledDateTimePicker
            control={control}
            name="plan_end_date"
            label={t("logistic.delivery_trip.plan_end_date")}
            viewMode={disabled}
          />
        </Grid>
        <Box width="100%" />
        <Grid item xs={12} sm={12} md={4}>
          <ControlledDateTimePicker
            control={control}
            name="actual_start_date"
            label={t("logistic.delivery_trip.actual_start_date")}
            viewMode={disabled}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={4}>
          <ControlledDateTimePicker
            control={control}
            name="actual_end_date"
            label={t("logistic.delivery_trip.actual_end_date")}
            viewMode={disabled}
          />
        </Grid>
      </Grid>
      <Typography fontWeight={600} my={1}>
        {t("logistic.delivery_trip.distance")}
      </Typography>
      <Grid container spacing={1.5}>
        <Grid item xs={12} sm={12} md={4}>
          <ControlledTextField
            control={control}
            name="route_code"
            label={t("logistic.delivery_trip.route_code")}
            error={Boolean(errors.route_code)}
            helperText={errors.route_code?.message}
            viewMode={disabled}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={4}>
          <ControlledNumberTextField
            fullWidth
            control={control}
            name="plan_distance"
            label={t("logistic.delivery_trip.plan_distance")}
            error={Boolean(errors.plan_distance)}
            helperText={errors.plan_distance?.message}
            viewMode={disabled}
            decimalScale={0}
          />
        </Grid>
        <Box width="100%" />
        <Grid
          item
          xs={12}
          sm={12}
          md={4}
          display={"flex"}
          alignItems={"flex-start"}
        >
          <ControlledNumberTextField
            fullWidth
            control={control}
            name="start_kilometer"
            label={t("logistic.delivery_trip.start_kilometer")}
            error={Boolean(errors.start_kilometer)}
            helperText={errors.start_kilometer?.message}
            onChange={(value) => {
              const end_kilometer = getValues("end_kilometer");
              if (end_kilometer) {
                setValue(
                  "total_kilometer_distance",
                  (end_kilometer || 0) - (value || 0)
                );
              } else {
                setValue("total_kilometer_distance", 0);
              }
            }}
            decimalScale={0}
            viewMode={disabled}
          />
          {!disabled && vehicleId && (
            <CustomizedTooltip title="เรียกเลขกิโลเมตรปัจจุบัน">
              <IconButton onClick={fetchCurrentKilo} color="primary">
                <RestartAltOutlinedIcon />
              </IconButton>
            </CustomizedTooltip>
          )}
        </Grid>
        <Grid item xs={12} sm={12} md={4}>
          <ControlledNumberTextField
            fullWidth
            control={control}
            name="end_kilometer"
            label={t("logistic.delivery_trip.end_kilometer")}
            error={Boolean(errors.end_kilometer)}
            helperText={errors.end_kilometer?.message}
            onChange={(value) => {
              const start_kilometer = getValues("start_kilometer");
              if (value) {
                setValue(
                  "total_kilometer_distance",
                  (value || 0) - (start_kilometer || 0)
                );
              } else {
                setValue("total_kilometer_distance", 0);
              }
            }}
            viewMode={disabled}
            decimalScale={0}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={4}>
          <ControlledNumberTextField
            fullWidth
            control={control}
            name="total_kilometer_distance"
            label={t("logistic.delivery_trip.total_kilometer_distance_actual")}
            disabled
            error={Boolean(errors.total_kilometer_distance)}
            helperText={errors.total_kilometer_distance?.message}
            allowNegative
            viewMode={disabled}
            decimalScale={0}
          />
        </Grid>
      </Grid>
      <Typography fontWeight={600} my={1}>
        {t("logistic.delivery_trip.temperature")}
      </Typography>
      <Grid container spacing={1.5}>
        <Grid item xs={12} sm={12} md={4}>
          <ControlledNumberTextField
            fullWidth
            control={control}
            name="temperature_start_hours"
            label={t("logistic.delivery_trip.temperature_start_hours")}
            error={Boolean(errors.temperature_start_hours)}
            helperText={errors.temperature_start_hours?.message}
            onChange={(value) => {
              const end = getValues("temperature_end_hours");
              if (end) {
                setValue("total_temperature_hours", end - (value || 0));
              } else {
                setValue("total_temperature_hours", 0);
              }
            }}
            viewMode={disabled}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={4}>
          <ControlledNumberTextField
            fullWidth
            control={control}
            name="temperature_end_hours"
            label={t("logistic.delivery_trip.temperature_end_hours")}
            error={Boolean(errors.temperature_end_hours)}
            helperText={errors.temperature_end_hours?.message}
            onChange={(value) => {
              const start = getValues("temperature_start_hours");
              if (value) {
                setValue("total_temperature_hours", (value || 0) - (start || 0));
              } else {
                setValue("total_temperature_hours", 0);
              }
            }}
            viewMode={disabled}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={4}>
          <ControlledNumberTextField
            fullWidth
            control={control}
            name="total_temperature_hours"
            label={t("logistic.delivery_trip.total_temperature_hours")}
            disabled
            error={Boolean(errors.total_temperature_hours)}
            helperText={errors.total_temperature_hours?.message}
            allowNegative
            viewMode={disabled}
          />
        </Grid>
      </Grid>
      <Typography fontWeight={600} my={1}>
        {t("logistic.delivery_trip.fuel_price")}
      </Typography>
      <Grid container spacing={1.5}>
        <Grid item xs={12} sm={12} md={4}>
          <ControlledNumberTextField
            fullWidth
            control={control}
            name="fuel_price"
            label={t("logistic.delivery_trip.fuel_price")}
            error={Boolean(errors.fuel_price)}
            helperText={errors.fuel_price?.message}
            viewMode={disabled}
            decimalScale={2}
          />
        </Grid>
      </Grid>
    </CustomizedBox>
  );
};

export default DateDistanceInfo;
